import { Moon, Sunrise, Sunset } from "lucide-react";
import { Link } from "react-router-dom";

const getMoonEmoji = (phase) => {
  const phaseLower = phase?.toLowerCase() || "";
  if (phaseLower.includes("new")) return "🌑";
  if (phaseLower.includes("waxing crescent")) return "🌒";
  if (phaseLower.includes("first quarter")) return "🌓";
  if (phaseLower.includes("waxing gibbous")) return "🌔";
  if (phaseLower.includes("full")) return "🌕";
  if (phaseLower.includes("waning gibbous")) return "🌖";
  if (phaseLower.includes("last quarter")) return "🌗";
  if (phaseLower.includes("waning crescent")) return "🌘";
  return "🌙";
};

export const MoonWidget = ({ moon, compact = false }) => {
  if (!moon) return null;

  if (compact) {
    return (
      <Link to="/moon" className="block" data-testid="moon-widget-compact">
        <div className="glass-card-hover p-4">
          <div className="flex items-center gap-4">
            <span className="text-4xl">{getMoonEmoji(moon.phase)}</span>
            <div>
              <p className="text-xs text-slate-500 uppercase tracking-widest font-heading mb-1">
                Moon Phase
              </p>
              <p className="text-lg font-heading font-semibold text-white">
                {moon.phase}
              </p>
              <p className="text-sm text-slate-400">
                {moon.illumination}% illuminated
              </p>
            </div>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <div className="glass-card p-6" data-testid="moon-widget">
      <h3 className="section-title mb-4">Luna Cycle</h3>
      
      {/* Moon Visual */}
      <div className="flex items-center justify-center mb-6">
        <div className="relative">
          <span className="text-8xl">{getMoonEmoji(moon.phase)}</span>
          <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 bg-white/10 rounded-full px-3 py-1">
            <span className="text-sm font-mono text-white">{moon.illumination}%</span>
          </div>
        </div>
      </div>

      <div className="text-center mb-6">
        <p className="text-xl font-heading font-bold text-white">{moon.phase}</p>
        <p className="text-sm text-slate-400">Day {moon.phase_day} of lunar cycle</p>
      </div>

      {/* Sun Times */}
      <div className="grid grid-cols-2 gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-amber-500/20">
            <Sunrise className="w-5 h-5 text-amber-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Sunrise</p>
            <p className="text-sm font-mono text-white">
              {moon.sunrise ? new Date(moon.sunrise).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-orange-500/20">
            <Sunset className="w-5 h-5 text-orange-400" strokeWidth={1.5} />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">Sunset</p>
            <p className="text-sm font-mono text-white">
              {moon.sunset ? new Date(moon.sunset).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export const MoonWidgetSkeleton = () => (
  <div className="glass-card p-6">
    <div className="skeleton h-6 w-32 rounded mb-4" />
    <div className="flex justify-center mb-6">
      <div className="skeleton w-24 h-24 rounded-full" />
    </div>
    <div className="space-y-2 mb-4">
      <div className="skeleton h-6 w-32 mx-auto rounded" />
      <div className="skeleton h-4 w-24 mx-auto rounded" />
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="skeleton h-12 rounded" />
      <div className="skeleton h-12 rounded" />
    </div>
  </div>
);
